import { firstUnusedColor } from '@shared/palette';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import { SwatchPicker } from '@/components/SwatchPicker';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import { useSaveProject } from '@/hooks/use-projects';
import { useTemplates } from '@/hooks/use-templates';
import type { AdminProject } from '@/types/projects';

const MAX_NAME_LENGTH = 100;

interface ProjectFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** null = create. */
  project: AdminProject | null;
  /** Colours of every ACTIVE project (drives the default colour and the "used" dots). */
  activeColors: readonly string[];
}

export function ProjectFormDialog({ open, onOpenChange, project, activeColors }: ProjectFormDialogProps): JSX.Element {
  const save = useSaveProject();
  const [name, setName] = useState('');
  const [color, setColor] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);
  // Sprint 11 (§9.4): create only. Default = the built-in Standard template.
  const templates = useTemplates(open && project === null);
  const [templateId, setTemplateId] = useState<string>('');
  const standardId = templates.data?.find((t) => t.builtinKey === 'standard')?.id ?? '';

  useEffect(() => {
    if (!open) return;
    setName(project?.name ?? '');
    setColor(project?.color ?? firstUnusedColor(activeColors));
    setNameError(null);
    setTemplateId('');
  }, [open, project, activeColors]);

  // A project's own colour is not "used by another project": drop one occurrence of it.
  const usedByOthers = useMemo(() => {
    if (!project) return activeColors;
    const others = [...activeColors];
    const own = others.indexOf(project.color);
    if (own >= 0) others.splice(own, 1);
    return others;
  }, [project, activeColors]);

  function onSubmit(event: FormEvent): void {
    event.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return setNameError('Enter a project name');
    if (trimmed.length > MAX_NAME_LENGTH) return setNameError(`Use at most ${MAX_NAME_LENGTH} characters`);
    save.mutate(
      { id: project?.id, name: trimmed, color, ...(project === null && (templateId || standardId) ? { templateId: templateId || standardId } : {}) },
      {
        onSuccess: () => {
          toast.success(project ? 'Project updated.' : 'Project created.');
          onOpenChange(false);
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={onSubmit} className="grid gap-3" noValidate>
          <DialogHeader>
            <DialogTitle>{project ? 'Edit project' : 'New project'}</DialogTitle>
            <DialogDescription>Names are unique among active projects.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-1.5">
            <Label htmlFor="project-name">Name</Label>
            <Input id="project-name" value={name} maxLength={MAX_NAME_LENGTH} onChange={(e) => setName(e.target.value)} />
            {nameError && <p className="text-xs text-danger-fg">{nameError}</p>}
          </div>
          <div className="grid gap-1.5">
            <span className="block text-xs leading-4 text-muted-foreground">Colour</span>
            <SwatchPicker value={color} onChange={setColor} usedColors={usedByOthers} />
          </div>
          {project === null && templates.data && (
            <div className="grid gap-1.5">
              <Label htmlFor="project-template">Template</Label>
              <NativeSelect id="project-template" value={templateId || standardId} onChange={(e) => setTemplateId(e.target.value)}>
                {templates.data.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </NativeSelect>
              <p className="text-xs text-muted-foreground">Copies the template's settings into the new project. Later template changes don't affect it.</p>
            </div>
          )}
          <DialogFooter>
            <Button type="submit" className="flex-1" disabled={save.isPending}>
              {save.isPending ? 'Saving…' : project ? 'Save changes' : 'Create project'}
            </Button>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
